import * as React from 'react';
import { Card, Input, Loading, Page, Table } from 'sha-el-design';
import { Colums } from 'sha-el-design/lib/components/Table/Table';
import { Link } from 'react-router-dom';
import * as moment from 'moment';
import { MdArrowBack, MdHome, MdLightbulbOutline } from 'react-icons/md';
import { FaPlay, FaPause } from 'react-icons/fa';
import { cssRaw } from 'typestyle';
import { API_ROOT, HEADER } from '../../config';
import {
  FMT, PRESETS, TODAY, RangePresets, activePresetKey,
  statTile, groupNum, shortDuration,
} from './reportBits';
import { CallDialog, RecentCall } from './CallDialog';
import '../Common/listPageStyles';

/**
 * Call Analytics -- the call logs imported from Diallogs.
 *
 * The same figures are also served as a standalone Django page at
 * <API_ROOT>/diallogs/. That page survives a broken frontend build and asks
 * for an API token by hand; this screen is the one people actually use, and
 * reuses the session token the app already holds, so there is nothing to paste.
 *
 * Everything here is READ-ONLY. Nothing on this screen writes to CallHistory
 * or to any Beat-Node table.
 */

// The vendor data only moves when sync_diallogs runs, so polling every few
// seconds would re-run seven COUNT queries per open tab to redraw identical
// numbers. A minute is well inside how fresh anyone needs this, and the
// Refresh button covers "I just ran the sync".
const POLL_MS = 60000;
// While a recording backup is running the progress bar needs fresher numbers
// than the minute-long feed poll gives; this poll is one small query and
// stops the moment the run ends.
const BACKUP_POLL_MS = 2000;
// Today first: the sync brings calls in every five minutes, so a same-day
// view is live here. The other report screens keep the shared list.
const RANGES = [TODAY].concat(PRESETS);

const capitalise = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

interface Caller { caller: string; calls: number; talk_minutes: number; }
interface ByType { label: string; count: number; }
interface CallerOption { id: number; name: string; calls: number; }
interface MismatchRow {
  at: string; caller: string; number: string; shop: string; status: string;
  type: string; severity: string; label: string; dials: number;
  longest_seconds: number; nearest_minutes: number; other_device: boolean;
}
interface CallerCheck {
  caller_id: number; caller: string; logged: number; checked: number;
  skipped: number; consistent: number; mismatched: number; mismatch_pct: number;
  by_type: { [key: string]: number };
}
interface Reconcile {
  available: boolean;
  logged: number; phone: number;
  scanned?: number; checked?: number; skipped?: number; consistent?: number;
  mismatched?: number; mismatch_pct?: number; other_device?: number;
  by_type?: { [key: string]: number };
  types?: { [key: string]: { severity: string; label: string } };
  callers?: CallerCheck[]; rows?: MismatchRow[]; rows_truncated?: boolean;
  phone_unlogged?: number; window_minutes?: number; truncated?: boolean;
}

// Short names for tables and chips. The full explanation of each type comes
// from the feed (`types`), so what counts as a mismatch is worded in one place
// -- beside the rules in views.py that decide it.
const MISMATCH_SHORT: { [key: string]: string } = {
  wrong_number_not_connected: 'Wrong no. — never connected',
  answered_not_connected: 'Answered — never connected',
  no_call_at_all: 'No call at all',
  no_reply_but_connected: 'No reply — but connected',
  no_call_in_window: 'Dialled at a different time',
};
const MISMATCH_ORDER = [
  'wrong_number_not_connected', 'answered_not_connected', 'no_call_at_all',
  'no_reply_but_connected', 'no_call_in_window',
];
// A clue to what an unknown number is (number_hints.py). `suggest` is a
// decision a reviewer can accept with one click; empty for label-only clues.
interface NumberHint {
  kind: string; text: string; suggest: string; retailer_id: number | null;
}
interface OffBook {
  number: string; national_number: string; name: string; callers: string;
  calls: number; answered: number; talk_seconds: number;
  first_at: string; last_at: string;
  decision: string; recording_url: string;
  hints: NumberHint[];
}
// What accepting each suggestion does, said as the button.
const SUGGEST_LABELS: { [key: string]: string } = {
  office: 'Mark as our office / colleague',
  wrong_number: 'Mark as wrong number',
  linked: 'Link to that shop',
};
const hasHint = (r: OffBook) => !!(r.hints && r.hints.length);
// The recording backup, as backup_monitor.snapshot() describes it. The text
// (headline, detail) is written server-side, so the screen and the alert email
// say the same thing.
interface BackupRun {
  id: number; status: string; trigger: string; by: string;
  started: string; finished: string;
  total: number; done: number; failed: number; refused: number; expired: number;
  handled: number; percent: number; message: string;
  reasons: { reason: string; count: number }[];
  alerted: boolean;
}
interface BackupStatus {
  health: string; problem: boolean; running: boolean;
  headline: string; detail: string;
  held: number; held_mb: number; in_window: number; window_days: number;
  last_ok: string; next_run: string; can_retry: boolean; alerts_to: number;
  run: BackupRun | null;
}
interface Feed {
  as_of: string; range: string; caller_id: number; callers: CallerOption[];
  total: number; connected: number;
  no_reply: number; talk_minutes: number; unique_numbers: number;
  // avg_talk_secs is per CONNECTED call; avg_calls_per_day divides by days
  // that had a call, not calendar days (see callreport.py).
  avg_talk_secs: number; active_days: number; avg_calls_per_day: number;
  with_recording: number; matched_shops: number; unattributed: number;
  by_type: ByType[]; per_caller: Caller[]; recent: RecentCall[];
  recorded_only: boolean; recent_truncated: boolean;
  insights_only: boolean; with_insights: number;
  off_book: OffBook[]; off_book_total: number; off_book_truncated: boolean;
  off_book_mode: string;
  off_book_counts: {
    all: number; answered: number; dead: number; decided: number; clues: number;
  };
  // Every decision ever recorded, not just those with a call in the window --
  // it is what the export contains.
  decisions_total: number;
  reconcile: Reconcile;
  sync: {
    minutes_ago: number; last_result: string;
    agents: number; agents_linked: number;
  };
  backup: BackupStatus;
}

interface State {
  // What the filter row currently shows.
  fromDate: string;
  toDate: string;
  caller: string;
  // What the figures on screen were actually fetched with. Split from the
  // draft above so a wide range is queried when the user asks for it, not on
  // every keystroke of a date they are still typing -- and so the 60s poll
  // keeps refreshing what is displayed rather than a half-typed filter.
  appliedFrom: string;
  appliedTo: string;
  appliedCaller: string;
  data: Feed;
  loading: boolean;
  error: string;
  playing: string;
  recordedOnly: boolean;
  offBookMode: string;
  tab: string;
  // Client-side narrowing of the discrepancy rows: clicking a caller in the
  // per-caller table filters to them without refetching.
  discCaller: string;
  discType: string;
  // Reviewing one unknown number: which number is open, and the shop search
  // running inside that panel.
  decideNumber: string;
  decideBusy: boolean;
  decideError: string;
  shopQuery: string;
  shopResults: any[];
  shopScope: string;
  shopBusy: boolean;
  // One-at-a-time review. Kept client-side: the queue walks the rows the feed
  // already returned, so working through it costs no extra requests.
  queueMode: boolean;
  doneNumbers: string[];
  skipNumbers: string[];
  doneCount: number;
  // Handing the decisions over as a file.
  exporting: boolean;
  exportError: string;
  // The caller scorecard download (per-caller KPIs for the applied dates).
  scoring: boolean;
  scoreError: string;
  // The call report download (the figures on screen, plus a day-by-day table).
  reporting: boolean;
  reportError: string;
  // The recording backup: its status as last seen -- from the feed, or from
  // the fast poll while a run is going -- and the panel around it.
  backup: BackupStatus;
  backupOpen: boolean;
  backupBusy: boolean;
  backupError: string;
  backupNotice: string;
  // The Calls list: only calls Diallogs wrote an AI summary for.
  insightsOnly: boolean;
  // The call open in the recording-and-summary modal, and whether it was
  // opened to play (the play button) or to read (the Summary button). Held as
  // the row itself, not an id to look up: the minute poll replaces the list,
  // and the modal must not vanish, or restart the audio, when it does.
  openCall: RecentCall;
  openToPlay: boolean;
}

cssRaw(`
  .cs-an__bar {
    display: flex; align-items: center; gap: 10px;
    flex-wrap: wrap; margin-bottom: 4px;
  }
  .cs-an__spacer { flex: 1 1 auto; }
  .cs-an__sync { font-size: 12.5px; color: #5A6E92; }
  .cs-an__sync b { color: #0F2547; }
  .cs-an__sync--stale { color: #A8630A; }
  .cs-an__sync--never { color: #B4232A; }
  .cs-an__refresh {
    padding: 7px 13px; border-radius: 9px; cursor: pointer;
    border: 1px solid rgba(15,37,71,0.12); background: rgba(255,255,255,0.8);
    color: #45587C; font: inherit; font-size: 12.5px; font-weight: 600;
    transition: background-color .18s ease, color .18s ease;
  }
  .cs-an__refresh:hover { background-color: rgba(30,111,217,0.09); color: #0F2547; }
  .cs-an__refresh:disabled { opacity: .55; cursor: default; }

  .cs-an__cols {
    display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 16px;
  }
  @media (max-width: 900px) { .cs-an__cols { grid-template-columns: 1fr; } }
  .cs-an__panel h4 {
    margin: 0 0 10px; font-size: 11.5px; font-weight: 700;
    letter-spacing: .09em; text-transform: uppercase; color: #7C93B5;
  }
  .cs-an__panel-head {
    display: flex; align-items: center; gap: 8px;
    flex-wrap: wrap; margin-bottom: 10px;
  }
  .cs-an__panel-head h4 { margin: 0; }
  .cs-an__panel-head .cs-chip:disabled { opacity: .45; cursor: default; }

  /* Matches the height and rhythm of sha-el's Input, which sits beside it. */
  .cs-an__label {
    display: block; font-size: 11px; font-weight: 600; color: #7C93B5;
    letter-spacing: .03em; margin-bottom: 4px;
  }
  .cs-an__select {
    width: 100%; height: 38px; padding: 0 30px 0 10px;
    border: 1px solid rgba(15,37,71,0.18); border-radius: 8px;
    background: #fff; color: #0F2547; font: inherit; font-size: 13.5px;
    cursor: pointer; appearance: none; -webkit-appearance: none;
    background-image:
      url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath fill='%237C93B5' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 11px center;
  }
  .cs-an__note {
    margin: 0 0 12px; padding: 9px 12px;
    border-left: 3px solid #38BDF8; border-radius: 0 8px 8px 0;
    background: rgba(56,189,248,0.07);
    font-size: 12.5px; line-height: 1.55; color: #46587B;
  }
  .cs-an__note b { color: #0F2547; font-weight: 650; }

  .cs-an__tabs {
    display: flex; gap: 4px; flex-wrap: wrap;
    margin: 18px 0 4px;
    border-bottom: 1px solid rgba(15,37,71,0.10);
  }
  .cs-an__tab {
    position: relative;
    display: inline-flex; align-items: center; gap: 7px;
    padding: 9px 14px; border: 0; background: transparent;
    border-radius: 9px 9px 0 0;
    font: inherit; font-size: 13.5px; font-weight: 600;
    color: #52658A; cursor: pointer;
    transition: color .18s ease, background-color .18s ease;
  }
  .cs-an__tab:hover { color: #0F2547; background: rgba(30,111,217,0.07); }
  .cs-an__tab--on { color: #0B3D91; }
  /* Sits ON the container border so the active tab reads as joined to the
     panel below it rather than as one more chip in a row. */
  .cs-an__tab--on::after {
    content: ''; position: absolute; left: 8px; right: 8px; bottom: -1px;
    height: 2px; border-radius: 2px;
    background: linear-gradient(90deg, #38BDF8 0%, #1E40AF 100%);
  }
  .cs-an__tab-n {
    display: inline-flex; align-items: center; justify-content: center;
    min-width: 20px; height: 18px; padding: 0 6px;
    border-radius: 999px; background: rgba(15,37,71,0.08);
    font-size: 11px; font-weight: 700; color: #52658A;
    font-variant-numeric: tabular-nums;
  }
  .cs-an__tab--on .cs-an__tab-n { background: rgba(30,111,217,0.15); color: #0B3D91; }
  .cs-an__tab:focus-visible { outline: 2px solid #2563EB; outline-offset: -2px; }
  @media (max-width: 620px) {
    .cs-an__tab { padding: 9px 10px; font-size: 12.5px; }
  }
  .cs-rep__num.ok { color: #1a7f4b; }
  .cs-an__panel .warn { color: #A8630A; font-size: 12.5px; }
  .cs-an__panel .bad { color: #B4232A; font-size: 12.5px; font-weight: 600; }

  .cs-an__sev {
    display: inline-block; padding: 2px 8px; border-radius: 999px;
    font-size: 11.5px; font-weight: 650; white-space: nowrap;
  }
  .cs-an__sev--high { color: #B4232A; background: rgba(180,35,42,0.09); }
  .cs-an__sev--medium { color: #A8630A; background: rgba(168,99,10,0.10); }
  .cs-an__callers { margin: 4px 0 6px; }
  .cs-an__caller-row { cursor: pointer; transition: background-color .15s ease; }
  .cs-an__caller-row:hover { background: rgba(30,111,217,0.06); }
  .cs-an__caller-row--on { background: rgba(30,111,217,0.11); }
  .cs-an__caller-row:focus-within { outline: 2px solid #2563EB; outline-offset: -2px; }

  .cs-an__decide {
    margin: 0 0 16px; padding: 14px 16px;
    border: 1px solid rgba(30,111,217,0.30); border-radius: 12px;
    background: rgba(30,111,217,0.04);
  }
  .cs-an__decide-actions {
    display: flex; gap: 6px; flex-wrap: wrap; margin: 4px 0 2px;
  }
  .cs-an__decide-btn {
    padding: 5px 11px; border-radius: 8px; cursor: pointer;
    border: 1px solid rgba(15,37,71,0.16); background: #fff;
    color: #1553A8; font: inherit; font-size: 12px; font-weight: 650;
    white-space: nowrap;
    transition: background-color .16s ease, color .16s ease;
  }
  .cs-an__decide-btn:hover {
    background: #1E40AF; color: #fff; border-color: transparent;
  }
  .cs-an__note--bad {
    border-left-color: #B4232A; background: rgba(180,35,42,0.06); color: #B4232A;
  }
  /* Clues to what an unknown number is: a quiet amber label in the list, and
     a box of them, with one-click suggestions, in the review panel. */
  .cs-an__hint {
    display: flex; align-items: center; gap: 4px; margin-top: 3px;
    font-size: 11.5px; line-height: 1.35; color: #8A5A00;
  }
  .cs-an__hint svg { flex: none; font-size: 13px; color: #C98A12; }
  .cs-an__clues {
    display: flex; flex-direction: column; gap: 6px;
    margin: 0 0 12px; padding: 10px 12px; border-radius: 10px;
    background: #FFF9EC; border: 1px solid rgba(201,138,18,0.22);
  }
  .cs-an__clues-h {
    font-size: 10.5px; font-weight: 700; letter-spacing: .07em;
    text-transform: uppercase; color: #A06B06;
  }
  .cs-an__clue {
    display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
    font-size: 13px; color: #5A4204;
  }
  .cs-an__clue svg { flex: none; font-size: 15px; color: #C98A12; }
  .cs-an__clue-text { flex: 1 1 200px; }
  .cs-an__clue .cs-chip { padding: 4px 11px; font-size: 12px; }
  .cs-an__clues-note { font-size: 11.5px; color: #A08450; }
  .cs-an__queue-bar {
    display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
    margin: 0 0 10px; font-size: 13px; color: #52658A;
  }
  .cs-an__queue-bar b { color: #0F2547; font-size: 15px; }
  .cs-an__queue-bar .ok { color: #1a7f4b; font-weight: 600; }

  .cs-an__select:focus {
    outline: none; border-color: #2563EB;
    box-shadow: 0 0 0 3px rgba(37,99,235,0.15);
  }
  .cs-an__mini { width: 100%; border-collapse: collapse; }
  .cs-an__mini td, .cs-an__mini th {
    padding: 7px 2px; font-size: 13px; text-align: left;
    border-bottom: 1px solid rgba(15,37,71,0.07);
  }
  .cs-an__mini th {
    font-size: 10.5px; font-weight: 700; letter-spacing: .07em;
    text-transform: uppercase; color: #7C93B5;
  }
  .cs-an__mini tr:last-child td { border-bottom: none; }
  .cs-an__mini .n { text-align: right; font-variant-numeric: tabular-nums; }

  .cs-an__shop { font-weight: 650; color: #0F2547; }
  .cs-an__num { font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 12.5px; }
  .cs-an__dim { color: #7C93B5; }

  .cs-an__play {
    display: inline-flex; align-items: center; justify-content: center;
    width: 30px; height: 30px; padding: 0; cursor: pointer;
    border: 1px solid rgba(15,37,71,0.14); border-radius: 50%;
    background: rgba(255,255,255,0.85); color: #1553A8; font-size: 11px;
    transition: background-color .18s ease, color .18s ease, transform .18s ease;
  }
  .cs-an__play:hover { background: #1E40AF; color: #fff; transform: translateY(-1px); }
  .cs-an__play--bad { color: #B4232A; border-color: rgba(180,35,42,0.35); cursor: default; }
  .cs-an__play--bad:hover { background: rgba(180,35,42,0.07); color: #B4232A; transform: none; }

  .cs-an__bk-chip {
    padding: 4px 10px; border-radius: 999px; cursor: pointer; white-space: nowrap;
    border: 1px solid rgba(26,127,75,0.25); background: rgba(26,127,75,0.07);
    color: #1a7f4b; font: inherit; font-size: 12px; font-weight: 600;
  }
  .cs-an__bk-chip--run {
    border-color: rgba(37,99,235,0.3); background: rgba(37,99,235,0.08); color: #1553A8;
  }
  .cs-an__bk-chip--bad {
    border-color: rgba(180,35,42,0.35); background: rgba(180,35,42,0.08); color: #B4232A;
  }
  .cs-an__bk {
    margin: 10px 0 4px; padding: 12px 14px; border-radius: 10px;
    border: 1px solid rgba(15,37,71,0.1); border-left-width: 4px;
    background: rgba(255,255,255,0.85);
  }
  .cs-an__bk--run { border-left-color: #2563EB; }
  .cs-an__bk--bad { border-left-color: #B4232A; background: rgba(180,35,42,0.05); }
  .cs-an__bk--ok { border-left-color: #1a7f4b; }
  .cs-an__bk-head {
    display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
    color: #0F2547; font-size: 13.5px;
  }
  .cs-an__bk--bad .cs-an__bk-head { color: #B4232A; }
  .cs-an__bk-meta { font-size: 12px; color: #7C93B5; }
  .cs-an__bk-text { margin: 6px 0 0; font-size: 12.5px; color: #45587C; }
  .cs-an__bk-dim { color: #7C93B5; }
  .cs-an__bk-bad { color: #B4232A; font-weight: 600; }
  .cs-an__bk-btn {
    padding: 6px 14px; border-radius: 9px; cursor: pointer; border: none;
    background: #B4232A; color: #fff; font: inherit; font-size: 12.5px; font-weight: 650;
  }
  .cs-an__bk--ok .cs-an__bk-btn { background: #1E40AF; }
  .cs-an__bk-btn:disabled { opacity: .6; cursor: default; }
  .cs-an__progress {
    height: 10px; margin-top: 10px; border-radius: 999px;
    background: rgba(15,37,71,0.08); overflow: hidden;
  }
  .cs-an__progress-fill {
    height: 100%; border-radius: 999px;
    background: linear-gradient(90deg, #2563EB, #38BDF8);
    transition: width .6s ease;
  }
  .cs-an__progress--wait .cs-an__progress-fill {
    background: repeating-linear-gradient(
      45deg, rgba(37,99,235,0.38) 0, rgba(37,99,235,0.38) 10px,
      rgba(37,99,235,0.14) 10px, rgba(37,99,235,0.14) 20px);
    background-size: 28px 28px;
    animation: cs-an-stripes 1s linear infinite;
  }
  @keyframes cs-an-stripes {
    from { background-position: 0 0; }
    to { background-position: 28px 0; }
  }

  .cs-an__ins-btn {
    padding: 4px 11px; border-radius: 999px; cursor: pointer; white-space: nowrap;
    border: 1px solid rgba(21,83,168,0.3); background: rgba(21,83,168,0.06);
    color: #1553A8; font: inherit; font-size: 12px; font-weight: 650;
  }
  .cs-an__ins-btn:hover {
    background: #1E40AF; color: #fff; border-color: transparent;
  }
`);

export class CallAnalytics extends React.Component<{}, State> {
  private timer: any = null;
  private audio: HTMLAudioElement = null;
  private backupTimer: any = null;
  // Set on unmount. A feed load or Retry answering after the user has left
  // must not start a fresh 2 s poll that nothing would ever stop.
  private unmounted = false;
  private decideRef = React.createRef<HTMLDivElement>();

  constructor(props: {}) {
    super(props);
    // Opens on the last 90 days, not today. The imported window is historical,
    // so defaulting to today showed an empty screen with nothing explaining
    // why -- the same trap the Call History report fell into.
    const from = moment().subtract(89, 'days').format(FMT);
    const to = moment().format(FMT);
    this.state = {
      fromDate: from, toDate: to, caller: '',
      appliedFrom: from, appliedTo: to, appliedCaller: '',
      data: null, loading: true, error: '', playing: '',
      recordedOnly: false,
      // Defaults to the numbers somebody actually spoke to: those are the
      // likely missing retailers, and the only view that leads to an action
      // without further filtering.
      offBookMode: 'answered',
      tab: 'calls',
      discCaller: '',
      discType: '',
      decideNumber: '',
      decideBusy: false,
      decideError: '',
      shopQuery: '',
      shopResults: [],
      shopScope: '',
      shopBusy: false,
      queueMode: false,
      doneNumbers: [],
      skipNumbers: [],
      doneCount: 0,
      exporting: false,
      exportError: '',
      scoring: false,
      scoreError: '',
      reporting: false,
      reportError: '',
      backup: null,
      backupOpen: false,
      backupBusy: false,
      backupError: '',
      backupNotice: '',
      insightsOnly: false,
      openCall: null,
      openToPlay: false,
    };
  }

  /** Follow a running backup: fresh numbers for the progress bar every 2 s. */
  watchBackup = () => {
    if (this.backupTimer || this.unmounted) { return; }
    this.backupTimer = setInterval(this.pollBackup, BACKUP_POLL_MS);
  }

  stopWatchingBackup = () => {
    if (this.backupTimer) { clearInterval(this.backupTimer); this.backupTimer = null; }
  }

  pollBackup = () => {
    fetch(`${API_ROOT}/api/diallogs/backup/`, {
      headers: { Authorization: HEADER.Authorization },
    })
      .then((r) => {
        if (!r.ok) { throw new Error(String(r.status)); }
        return r.json();
      })
      .then((b: BackupStatus) => {
        if (this.unmounted) { return; }
        const wasRunning = !!(this.state.backup && this.state.backup.running);
        this.setState({ backup: b });
        if (b.running) { return; }
        this.stopWatchingBackup();
        if (wasRunning) {
          // Watched to the end: say how it went, then refresh the page's
          // figures once. A failure needs no notice -- the alert shows itself.
          this.setState({
            backupNotice: !b.problem && b.run ? `Backup finished: ${b.run.message}.` : '',
          });
          this.load(true);
        }
      })
      // A missed poll is not worth an error: the next one, or the minute
      // feed load, catches up.
      .catch(() => undefined);
  }

  /**
   * Start a backup run now. The server starts it in its own process and
   * answers at once -- the run never happens inside this request -- so this
   * only has to begin following it.
   */
  retryBackup = () => {
    this.setState({ backupBusy: true, backupError: '', backupNotice: '', backupOpen: true });
    fetch(`${API_ROOT}/api/diallogs/backup/retry/`, {
      method: 'POST',
      headers: { Authorization: HEADER.Authorization },
    })
      .then((r) => r.json()
        .catch(() => ({}))
        .then((body: any) => ({ status: r.status, body })))
      .then((res) => {
        if (this.unmounted) { return; }
        if (res.status === 401) {
          throw new Error('Your session was rejected. Log out and back in.');
        }
        if (res.body.backup) { this.setState({ backup: res.body.backup }); }
        // 202: started. 409: one is already running -- follow that one
        // instead of starting a second.
        if (res.status === 202 || res.status === 409) {
          this.setState({ backupBusy: false });
          this.watchBackup();
          return;
        }
        throw new Error(res.body.error || `The server returned ${res.status}.`);
      })
      .catch((e) => this.setState({
        backupBusy: false,
        backupError: e.message || 'Could not start the backup.',
      }));
  }

  /**
   * Download the decisions as a CSV, for whoever maintains the retailer
   * master in Beat-Node.
   *
   * Fetched rather than linked: the endpoint authenticates on the session
   * token in a header, and a plain <a href> sends no headers -- it would
   * arrive as a 401 rendered into the tab. So pull the bytes, then hand them
   * to the browser as a file.
   */
  downloadExport = () => {
    this.setState({ exporting: true, exportError: '' });

    fetch(`${API_ROOT}/api/diallogs/export/`, {
      headers: { Authorization: HEADER.Authorization },
    })
      .then((r) => {
        if (r.status === 401) {
          throw new Error('Your session was rejected. Log out and back in.');
        }
        if (!r.ok) {
          throw new Error(`The server could not build the file (${r.status}).`);
        }
        return r.blob();
      })
      .then((blob) => {
        this.saveFile(blob, `retailer-corrections-${moment().format('YYYY-MM-DD')}.csv`);
        this.setState({ exporting: false });
      })
      .catch((e) => this.setState({
        exporting: false,
        exportError: e.message || 'The download failed.',
      }));
  }

  /** Hand fetched bytes to the browser as a download. */
  saveFile = (blob: Blob, name: string) => {
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.download = name;
    // Firefox only follows a click on a link that is in the document.
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Revoking straight away cancels the save in some browsers; the object
    // is a few KB and the tab will drop it on unload regardless.
    setTimeout(() => URL.revokeObjectURL(href), 30000);
  }

  /**
   * The caller scorecard for the dates on screen: per-caller KPIs from the
   * phone logs, as a PDF that explains itself (see scorecard.py). Fetched
   * for the same reason as the export above -- the token rides in a header.
   */
  downloadScorecard = () => {
    const { appliedFrom, appliedTo, appliedCaller } = this.state;
    this.setState({ scoring: true, scoreError: '' });
    fetch(`${API_ROOT}/api/diallogs/scorecard/`
          + `?from=${encodeURIComponent(appliedFrom)}&to=${encodeURIComponent(appliedTo)}`
          + (appliedCaller ? `&caller=${encodeURIComponent(appliedCaller)}` : ''), {
      headers: { Authorization: HEADER.Authorization },
    })
      .then((r) => {
        if (r.status === 401) {
          throw new Error('Your session was rejected. Log out and back in.');
        }
        if (r.status === 403) {
          throw new Error('The scorecard is for admins and supervisors.');
        }
        if (!r.ok) {
          throw new Error(`The server could not build the scorecard (${r.status}).`);
        }
        return r.blob();
      })
      .then((blob) => {
        if (this.unmounted) { return; }
        this.saveFile(blob, `caller-scorecard-${appliedFrom}-to-${appliedTo}.pdf`);
        this.setState({ scoring: false });
      })
      .catch((e) => {
        if (this.unmounted) { return; }
        this.setState({ scoring: false, scoreError: e.message || 'The download failed.' });
      });
  }

  /**
   * The call report for the dates on screen: the same totals as the tiles,
   * then one row per day -- calls, connected, talk time (see callreport.py).
   */
  downloadReport = () => {
    const { appliedFrom, appliedTo, appliedCaller } = this.state;
    this.setState({ reporting: true, reportError: '' });
    fetch(`${API_ROOT}/api/diallogs/report/`
          + `?from=${encodeURIComponent(appliedFrom)}&to=${encodeURIComponent(appliedTo)}`
          + (appliedCaller ? `&caller=${encodeURIComponent(appliedCaller)}` : ''), {
      headers: { Authorization: HEADER.Authorization },
    })
      .then((r) => {
        if (r.status === 401) {
          throw new Error('Your session was rejected. Log out and back in.');
        }
        if (r.status === 403) {
          throw new Error('The call report is for admins and supervisors.');
        }
        if (!r.ok) {
          throw new Error(`The server could not build the report (${r.status}).`);
        }
        return r.blob();
      })
      .then((blob) => {
        if (this.unmounted) { return; }
        this.saveFile(blob, `call-report-${appliedFrom}-to-${appliedTo}.pdf`);
        this.setState({ reporting: false });
      })
      .catch((e) => {
        if (this.unmounted) { return; }
        this.setState({ reporting: false, reportError: e.message || 'The download failed.' });
      });
  }

  rangeIsValid = () => {
    const { fromDate, toDate } = this.state;
    return !!fromDate && !!toDate && !moment(fromDate).isAfter(moment(toDate));
  }

  isDirty = () => {
    const { fromDate, toDate, caller, appliedFrom, appliedTo, appliedCaller } = this.state;
    return fromDate !== appliedFrom
      || toDate !== appliedTo
      || caller !== appliedCaller;
  }

  /** Display name for a caller id, from the list the feed supplied. */
  callerName = (id: string) => {
    const { data } = this.state;
    if (!id || !data) { return ''; }
    const hit = data.callers.filter((c) => String(c.id) === String(id))[0];
    return hit ? hit.name : '';
  }

  search = () => {
    if (!this.rangeIsValid()) { return; }
    const { fromDate, toDate, caller } = this.state;
    this.setState(
      { appliedFrom: fromDate, appliedTo: toDate, appliedCaller: caller },
      () => this.load(),
    );
  }

  onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { this.search(); }
  }

  applyPreset = (preset: typeof PRESETS[0]) => {
    const [a, b] = preset.range();
    const fromDate = a.format(FMT);
    const toDate = b.format(FMT);
    // Presets apply immediately: picking one IS the decision, so making the
    // user then press Search would be a second click for no added intent.
    this.setState(
      { fromDate, toDate, appliedFrom: fromDate, appliedTo: toDate },
      () => this.load(),
    );
  }

  componentDidMount() {
    this.load();
    this.timer = setInterval(() => this.load(true), POLL_MS);
  }

  componentWillUnmount() {
    // Both of these outlive the component otherwise: the interval keeps
    // fetching after the user navigates away, and the audio keeps playing.
    this.unmounted = true;
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
    this.stopWatchingBackup();
    this.stopAudio();
  }

  load = (quiet?: boolean) => {
    const {
      appliedFrom, appliedTo, appliedCaller, recordedOnly, insightsOnly, offBookMode,
    } = this.state;
    const callerId = appliedCaller || '';
    if (!quiet) { this.setState({ loading: true, error: '' }); }

    const url = `${API_ROOT}/api/diallogs/feed/`
      + `?from=${encodeURIComponent(appliedFrom)}&to=${encodeURIComponent(appliedTo)}`
      + (callerId ? `&caller=${encodeURIComponent(callerId)}` : '')
      + (recordedOnly ? '&recorded=1' : '')
      + (insightsOnly ? '&insights=1' : '')
      + `&off_book=${encodeURIComponent(offBookMode)}`;

    fetch(url, {
      headers: { Authorization: HEADER.Authorization },
    })
      .then((r) => {
        if (r.status === 401) {
          throw new Error('Your session was rejected. Log out and back in.');
        }
        if (!r.ok) { throw new Error(`The server returned ${r.status}.`); }
        return r.json();
      })
      .then((data: Feed) => {
        // A slow response for a filter the user has already moved off would
        // otherwise overwrite what they are now looking at.
        if (this.state.appliedFrom !== appliedFrom
            || this.state.appliedTo !== appliedTo
            || this.state.recordedOnly !== recordedOnly
            || this.state.insightsOnly !== insightsOnly
            || this.state.offBookMode !== offBookMode
            || (this.state.appliedCaller || '') !== callerId) {
          return;
        }
        if (this.unmounted) { return; }
        const wasRunning = !!(this.state.backup && this.state.backup.running);
        const b = data.backup || null;
        this.setState({ data, backup: b, loading: false, error: '' });
        // A run already going when the page loads -- the nightly one, or a
        // Retry from another tab -- gets a live progress bar too.
        if (b && b.running) { this.watchBackup(); }
        // This load, not the 2 s poll, can be the one that sees a run end: say
        // how it went here too, or the notice is lost.
        if (wasRunning && b && !b.running && !b.problem && b.run) {
          this.setState({ backupNotice: `Backup finished: ${b.run.message}.` });
        }
      })
      .catch((e) => this.setState({ loading: false, error: e.message }));
  }

  openDecide = (row: OffBook) => {
    // Whatever is playing belongs to the row list behind the panel.
    this.stopAudio();
    this.setState({
      decideNumber: row.national_number, decideError: '', decideBusy: false,
      shopQuery: '', shopResults: [], shopScope: '', shopBusy: true, playing: '',
    }, () => {
      // The panel is drawn above a list that can run to 500 rows, so opening
      // it from a row further down left it off-screen -- which reads exactly
      // like the button doing nothing at all.
      const el = this.decideRef.current;
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      this.searchShops('');
    });
  }

  closeDecide = () => this.setState({
    decideNumber: '', shopResults: [], shopScope: '', decideError: '',
  })

  searchShops = (query: string) => {
    const number = this.state.decideNumber;
    if (!number) { return; }
    this.setState({ shopBusy: true });

    fetch(`${API_ROOT}/api/diallogs/retailers/?number=${encodeURIComponent(number)}`
          + (query ? `&q=${encodeURIComponent(query)}` : ''),
          { headers: { Authorization: HEADER.Authorization } })
      .then((r) => r.json())
      .then((d) => {
        // Ignore a reply for a number the reviewer has already moved off.
        if (this.state.decideNumber !== number) { return; }
        this.setState({
          shopResults: d.results || [], shopScope: d.scope || '', shopBusy: false,
        });
      })
      .catch(() => this.setState({ shopBusy: false }));
  }

  saveDecision = (status: string, retailerId?: number) => {
    const number = this.state.decideNumber;
    this.setState({ decideBusy: true, decideError: '' });

    fetch(`${API_ROOT}/api/diallogs/decide/`, {
      method: 'POST',
      headers: {
        Authorization: HEADER.Authorization,
        'content-type': 'application/json',
      },
      body: JSON.stringify({ number, status, retailer_id: retailerId }),
    })
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then((res) => {
        if (!res.ok) { throw new Error(res.d.error || 'Could not save that decision'); }
        // An undo puts the number back in play, so it must not count as done.
        const undone = status === 'undo';
        this.setState({
          decideNumber: '', decideBusy: false, shopResults: [], shopScope: '',
          doneNumbers: undone
            ? this.state.doneNumbers
            : this.state.doneNumbers.concat([number]),
          doneCount: this.state.doneCount + (undone ? 0 : 1),
        }, () => {
          // Refresh counts quietly. The queue keeps its place because it
          // tracks numbers, not row positions, so a reload cannot lose it.
          this.load(true);
          if (this.state.queueMode && !undone) { this.advanceQueue(); }
        });
      })
      .catch((e) => this.setState({ decideBusy: false, decideError: e.message }));
  }

  /** The next number still needing an answer, walking the rows already loaded. */
  queueRow = (data: Feed) => this.remainingInQueue(data)[0]

  remainingInQueue = (data: Feed) => {
    const { doneNumbers, skipNumbers } = this.state;
    return data.off_book.filter((r) =>
      doneNumbers.indexOf(r.national_number) === -1
      && skipNumbers.indexOf(r.national_number) === -1);
  }

  setQueueMode = (queueMode: boolean, data: Feed) => {
    if (!queueMode) {
      this.setState({ queueMode: false }, this.closeDecide);
      return;
    }
    // A fresh pass starts with nothing skipped.
    this.setState({ queueMode: true, skipNumbers: [] }, () => {
      const row = this.queueRow(data);
      if (row) { this.openDecide(row); }
    });
  }

  advanceQueue = () => {
    const data = this.state.data;
    const row = data ? this.queueRow(data) : null;
    if (row) {
      this.openDecide(row);
    } else {
      this.setState({ decideNumber: '' });
    }
  }

  skipInQueue = () => {
    const number = this.state.decideNumber;
    if (!number) { return; }
    this.setState({
      skipNumbers: this.state.skipNumbers.concat([number]),
      decideNumber: '',
    }, this.advanceQueue);
  }

  componentDidUpdate() {
    // Keeps the queue on its feet: after a reload, or if the open number
    // disappears, it opens the next one rather than showing a blank card.
    // Safe from looping because openDecide sets decideNumber.
    const { queueMode, decideNumber, data } = this.state;
    if (queueMode && data && !decideNumber) {
      const row = this.queueRow(data);
      if (row) { this.openDecide(row); }
    }
  }

  renderQueue = (data: Feed) => {
    const { doneCount, skipNumbers } = this.state;
    const left = this.remainingInQueue(data).length;

    if (!this.queueRow(data)) {
      return (
        <div className='cs-list__empty'>
          <h4>Nothing left to identify here</h4>
          <p>
            {doneCount
              ? `You identified ${groupNum(doneCount)} number${doneCount === 1 ? '' : 's'} just now. `
              : 'Every number in this view has been identified already. '}
            {skipNumbers.length
              ? `${groupNum(skipNumbers.length)} skipped — go back to the list to revisit them.`
              : 'Try another view, or a wider date range.'}
          </p>
        </div>
      );
    }

    return (
      <>
        <div className='cs-an__queue-bar'>
          <span><b>{groupNum(left)}</b> left in this view</span>
          {doneCount
            ? <span className='ok'>{groupNum(doneCount)} done just now</span>
            : null}
          <span className='cs-an__spacer' />
          <button type='button' className='cs-chip' onClick={this.skipInQueue}>
            Skip for now
          </button>
        </div>
        {this.renderDecide(data)}
      </>
    );
  }

  renderDecide = (data: Feed) => {
    const {
      decideNumber, shopResults, shopScope, shopBusy, shopQuery,
      decideBusy, decideError,
    } = this.state;
    if (!decideNumber) { return null; }
    const row = data.off_book.filter((r) => r.national_number === decideNumber)[0];
    if (!row) {
      // Never fail silently. If the row has dropped out of the current view --
      // the range, caller or chip changed since it was opened -- say so rather
      // than render nothing and leave the button looking broken.
      return (
        <div className='cs-an__decide' ref={this.decideRef}>
          <p className='cs-an__note'>
            <b>{decideNumber}</b> is not in the list any more — the dates, caller
            or view changed since you opened it.{' '}
            <button type='button' className='cs-an__refresh' onClick={this.closeDecide}>
              Close
            </button>
          </p>
        </div>
      );
    }

    const choices = [
      { key: 'new_shop', label: 'A shop, not in the master' },
      { key: 'personal', label: 'Personal call' },
      { key: 'office', label: 'Our office / colleague' },
      { key: 'wrong_number', label: 'Wrong number' },
    ];

    return (
      <div className='cs-an__decide' ref={this.decideRef}>
        <div className='cs-an__panel-head'>
          <h4>Identify {row.number}{row.name ? ` — ${row.name}` : ''}</h4>
          <span className='cs-an__spacer' />
          {row.recording_url && (
            <button
              type='button'
              className='cs-an__play'
              title='Play the longest recording for this number'
              onClick={() => this.play(row.recording_url)}
            >
              {this.state.playing === row.recording_url ? <FaPause /> : <FaPlay />}
            </button>
          )}
          <button
            type='button'
            className='cs-an__refresh'
            onClick={() => (this.state.queueMode
              ? this.setQueueMode(false, data)
              : this.closeDecide())}
          >
            {this.state.queueMode ? 'Back to the list' : 'Close'}
          </button>
        </div>

        <p className='cs-an__note'>
          {groupNum(row.calls)} attempt{row.calls === 1 ? '' : 's'},{' '}
          {groupNum(row.answered)} answered, {shortDuration(row.talk_seconds)} of talk
          {row.callers ? `, by ${row.callers}` : ''}.
          {row.decision ? ` Currently marked: ${row.decision.replace(/_/g, ' ')}.` : ''}
          {row.recording_url
            ? ' Play the recording — the first few seconds usually name the shop.'
            : ' No recording for this number, so go on the caller and the town.'}
        </p>

        {hasHint(row) ? (
          <div className='cs-an__clues'>
            <span className='cs-an__clues-h'>Clues</span>
            {row.hints.map((h, i) => (
              <div className='cs-an__clue' key={i}>
                <MdLightbulbOutline />
                <span className='cs-an__clue-text'>{h.text}</span>
                {h.suggest && SUGGEST_LABELS[h.suggest] ? (
                  <button
                    type='button'
                    className='cs-chip cs-chip--on'
                    disabled={decideBusy}
                    onClick={() => this.saveDecision(h.suggest, h.retailer_id || undefined)}
                  >
                    {SUGGEST_LABELS[h.suggest]}
                  </button>
                ) : null}
              </div>
            ))}
            <span className='cs-an__clues-note'>
              A clue is a likely answer, not a certain one: check it before accepting.
            </span>
          </div>
        ) : null}

        {decideError && <p className='cs-an__note cs-an__note--bad'>{decideError}</p>}

        <div className='cs-an__decide-actions'>
          {choices.map((c) => (
            <button
              key={c.key}
              type='button'
              className='cs-chip'
              disabled={decideBusy}
              onClick={() => this.saveDecision(c.key)}
            >
              {c.label}
            </button>
          ))}
          {row.decision && (
            <button
              type='button'
              className='cs-chip cs-chip--danger'
              disabled={decideBusy}
              onClick={() => this.saveDecision('undo')}
            >
              Undo
            </button>
          )}
        </div>

        <div className='cs-rep__filters' style={{ marginTop: 12 }}>
          <div className='cs-rep__field'>
            <Input
              label='Or link it to a shop — search by name'
              value={shopQuery}
              onChange={(e) => this.setState({ shopQuery: e.target.value })}
            />
          </div>
          <button
            type='button'
            className='cs-rep__search'
            onClick={() => this.searchShops(shopQuery)}
            disabled={shopBusy}
          >
            {shopBusy ? 'Searching…' : 'Search'}
          </button>
        </div>

        {shopScope && <p className='cs-rep__muted'>Showing {shopScope}.</p>}

        <table className='cs-an__mini'>
          <tbody>
            {shopResults.map((s) => (
              <tr key={s.id}>
                <td>
                  <b>{s.firm}</b>
                  <div className='cs-an__dim'>
                    {s.proprietor}{s.town ? ` · ${s.town}` : ''}
                    {s.numbers && s.numbers.length ? ` · ${s.numbers.join(', ')}` : ''}
                  </div>
                </td>
                <td className='n'>
                  <button
                    type='button'
                    className='cs-chip cs-chip--on'
                    disabled={decideBusy}
                    onClick={() => this.saveDecision('linked', s.id)}
                  >
                    {s.holds_this_number ? 'Link (already listed)' : 'Link to this shop'}
                  </button>
                </td>
              </tr>
            ))}
            {!shopBusy && !shopResults.length && (
              <tr>
                <td className='cs-an__dim'>
                  No shops to show yet — type a name and press Search.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  }

  setOffBookMode = (offBookMode: string) => {
    if (offBookMode === this.state.offBookMode) { return; }
    this.setState({ offBookMode }, () => this.load());
  }

  /** Which calls the Calls list shows: most recent, with a recording, or
   *  with an AI summary. */
  setListMode = (mode: string) => {
    const recordedOnly = mode === 'recorded';
    const insightsOnly = mode === 'insights';
    if (recordedOnly === this.state.recordedOnly
        && insightsOnly === this.state.insightsOnly) { return; }
    // Stop whatever is playing and close any open call: the rows they belong
    // to are about to be replaced.
    this.stopAudio();
    this.setState({ recordedOnly, insightsOnly, playing: '', openCall: null },
                  () => this.load());
  }

  /** Open one call in the modal: to play it, or to read its summary. */
  showCall = (c: RecentCall, toPlay: boolean) => {
    // The modal has its own player; anything playing on the page stops.
    this.stopAudio();
    this.setState({ openCall: c, openToPlay: toPlay, playing: '' });
  }

  hideCall = () => this.setState({ openCall: null });

  stopAudio = () => {
    if (this.audio) { this.audio.pause(); this.audio = null; }
  }

  play = (url: string) => {
    if (this.state.playing === url) {
      this.stopAudio();
      this.setState({ playing: '' });
      return;
    }
    // One shared element, created on demand. Fifty <audio> tags would have the
    // browser open fifty connections for recordings nobody plays.
    this.stopAudio();
    const audio = new Audio(url);
    this.audio = audio;
    audio.onended = () => this.setState({ playing: '' });
    audio.onerror = () => this.setState({ playing: 'error:' + url });
    audio.play();
    this.setState({ playing: url });
  }

  breadcrumbs = () => ({
    seperator: '/',
    paths: [
      { text: <MdHome />, link: '/dashboard' },
      { text: 'Call Analytics', link: '/dashboard/call_analytics' },
    ].map((v) => ({
      render: () => <Link key={v.link} to={v.link}>{v.text}</Link>,
    })),
  })

  syncLine = () => {
    const s = this.state.data.sync;
    let cls = '';
    let text: string;
    if (s.minutes_ago === null || s.minutes_ago === undefined) {
      cls = ' cs-an__sync--never';
      text = 'never synced';
    } else if (s.minutes_ago > 2880) {
      cls = ' cs-an__sync--never';
      text = `last synced ${Math.floor(s.minutes_ago / 1440)} days ago`;
    } else if (s.minutes_ago > 1500) {
      cls = ' cs-an__sync--stale';
      text = `last synced ${Math.floor(s.minutes_ago / 60)} hrs ago`;
    } else {
      text = s.minutes_ago < 60
        ? `synced ${s.minutes_ago} min ago`
        : `synced ${Math.floor(s.minutes_ago / 60)} hrs ago`;
    }
    // Report attribution, not an agent ratio. "2/5 callers mapped" read as a
    // fault when the three unmapped Diallogs accounts were an admin login and
    // two vendor demo users with no calls between them -- the number went down
    // as accounts were added, never because anything was wrong. What actually
    // matters is whether any call could not be traced back to a caller, so the
    // agent counts move to the tooltip.
    const d = this.state.data;
    let attribution = null;
    if (d.total) {
      attribution = d.unattributed
        ? <span className='cs-an__sync--stale'>
            {groupNum(d.unattributed)} of {groupNum(d.total)} calls unattributed
          </span>
        : <span>all {groupNum(d.total)} calls attributed</span>;
    }

    return (
      <span
        className={'cs-an__sync' + cls}
        title={`${s.agents_linked} of ${s.agents} Diallogs accounts are linked `
               + `to a Calling Soul user. An unlinked account only matters if it `
               + `has calls -- those show as unattributed.`}
      >
        <b>{text}</b>{attribution ? <> · {attribution}</> : null}
      </span>
    );
  }

  /** The backup's one-word state on the status bar; click to open details. */
  backupChip = () => {
    const b = this.state.backup;
    if (!b) { return null; }
    let cls = 'cs-an__bk-chip';
    let icon = '✓';
    if (b.running) {
      cls += ' cs-an__bk-chip--run';
      icon = '⟳';
    } else if (b.problem) {
      cls += ' cs-an__bk-chip--bad';
      icon = '⚠';
    }
    return (
      <button
        type='button'
        className={cls}
        title={b.detail}
        aria-expanded={this.state.backupOpen}
        onClick={() => this.setState({
          backupOpen: !this.state.backupOpen, backupNotice: '', backupError: '',
        })}
      >
        {icon} {b.headline}
      </button>
    );
  }

  /**
   * The backup panel. Opens by itself while a run is going (progress bar) or
   * when something is wrong (alert with Retry); otherwise only on request.
   */
  renderBackup = () => {
    const b = this.state.backup;
    const { backupOpen, backupBusy, backupError, backupNotice } = this.state;
    if (!b) { return null; }
    if (!(backupOpen || b.problem || b.running || backupNotice || backupError)) {
      return null;
    }
    const run = b.run;
    const error = backupError
      ? <p className='cs-an__bk-text cs-an__bk-bad'>{backupError}</p>
      : null;
    const retry = b.can_retry ? (
      <button
        type='button'
        className='cs-an__bk-btn'
        onClick={this.retryBackup}
        disabled={backupBusy}
      >
        {backupBusy ? 'Starting…' : (b.problem ? 'Retry now' : 'Back up now')}
      </button>
    ) : null;

    if (b.running && run) {
      const counted = run.total > 0;
      const pct = counted ? run.percent : 0;
      const missed = run.failed + run.refused;
      return (
        <div className='cs-an__bk cs-an__bk--run' role='status' aria-live='polite'>
          <div className='cs-an__bk-head'>
            <b>{run.status === 'starting'
              ? 'Starting the recording backup…'
              : 'Backing up call recordings…'}</b>
            <span className='cs-an__spacer' />
            <span className='cs-an__bk-meta'>
              {run.trigger === 'manual'
                ? `started ${run.started}${run.by ? ` by ${run.by}` : ''}`
                : `nightly run, started ${run.started}`}
            </span>
          </div>
          <div
            className={'cs-an__progress' + (counted ? '' : ' cs-an__progress--wait')}
            role='progressbar'
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={pct}
          >
            <div className='cs-an__progress-fill' style={{ width: `${counted ? pct : 100}%` }} />
          </div>
          <p className='cs-an__bk-text'>
            {counted ? (
              <>
                <b>{groupNum(run.handled)}</b> of <b>{groupNum(run.total)}</b> recordings
                {' '}({pct}%)
                {missed ? (
                  <> · <span className='cs-an__bk-bad'>
                    {groupNum(missed)} could not be saved so far
                  </span></>
                ) : null}
              </>
            ) : 'Checking which recordings need saving…'}
          </p>
          {error}
        </div>
      );
    }

    if (b.problem) {
      return (
        <div className='cs-an__bk cs-an__bk--bad' role='alert'>
          <div className='cs-an__bk-head'>
            <b>⚠ {capitalise(b.headline)}</b>
            <span className='cs-an__spacer' />
            {retry}
          </div>
          <p className='cs-an__bk-text'>{b.detail}</p>
          {run && run.reasons && run.reasons.length ? (
            <p className='cs-an__bk-text'>
              Why: {run.reasons.map((r) => `${r.reason} (${groupNum(r.count)})`).join('; ')}
            </p>
          ) : null}
          <p className='cs-an__bk-text cs-an__bk-dim'>
            Nothing already backed up has been lost.{' '}
            {/* Only promise a retry-by-itself where one will happen. "Overdue"
                means the nightly run itself is not happening, so waiting for
                it would just lose another day. Not-set-up and misconfigured
                need the server fixed; their detail already says so. */}
            {b.health === 'overdue'
              ? `The nightly run at ${b.next_run} has not completed`
                + `${b.last_ok ? ` since ${b.last_ok}` : ''}: have the server's scheduled `
                + `job checked${b.can_retry ? '. Retry now makes a backup meanwhile.' : '.'}`
              : (b.health === 'failed' || b.health === 'never')
                ? `The nightly run at ${b.next_run} tries again by itself`
                  + `${b.can_retry ? ', or press Retry now.' : '.'}`
                : ''}
            {b.alerts_to
              ? ` A failed nightly run is also emailed to ${b.alerts_to} `
                + `${b.alerts_to === 1 ? 'person' : 'people'}.`
              : ''}
          </p>
          {error}
        </div>
      );
    }

    // Healthy: shown when opened from the chip, or right after a run ended.
    return (
      <div className='cs-an__bk cs-an__bk--ok'>
        <div className='cs-an__bk-head'>
          <b>{backupNotice || '✓ Call recordings are backed up'}</b>
          <span className='cs-an__spacer' />
          {retry}
          <button
            type='button'
            className='cs-an__refresh'
            onClick={() => this.setState({ backupOpen: false, backupNotice: '', backupError: '' })}
          >
            Close
          </button>
        </div>
        <p className='cs-an__bk-text'>{b.detail}</p>
        <p className='cs-an__bk-text cs-an__bk-dim'>
          A copy of every recording is kept on our server for {b.window_days} days, then
          deleted automatically. Calling Soul still plays recordings from Diallogs.
          Next backup: {b.next_run} tonight.
        </p>
        {error}
      </div>
    );
  }

  // A zero duration is a fact, not a gap: the call rang and nobody picked up.
  // Rendering a dash made hundreds of real zeros look like missing data.
  duration = (c: RecentCall) => {
    if (!c.duration) {
      return (
        <span className='cs-rep__num cs-rep__num--zero'>
          {c.type === 'Missed' ? 'missed' : 'no answer'}
        </span>
      );
    }
    return <span className='cs-rep__num'>{shortDuration(c.duration)}</span>;
  }

  /** What the phone record actually shows, in words, for one mismatch row. */
  phoneShows = (r: MismatchRow) => {
    let text: string;
    if (r.type === 'no_call_at_all') {
      text = 'No call to this number';
    } else if (r.type === 'no_call_in_window') {
      text = `Nearest dial ${groupNum(r.nearest_minutes)} min away`;
    } else if (r.type === 'no_reply_but_connected') {
      text = `Connected for ${shortDuration(r.longest_seconds)}`;
    } else {
      text = `${r.dials} dial${r.dials === 1 ? '' : 's'}, none connected`;
    }
    return (
      <div>
        <span>{text}</span>
        {r.other_device && <div className='cs-an__dim'>from another caller’s phone</div>}
      </div>
    );
  }

  mismatchColumns = (): Array<Colums<MismatchRow>> => [{
    key: 'at',
    header: 'Marked at',
    render: (t, r) => <span className='cs-an__num'>{r.at}</span>,
  }, {
    key: 'caller',
    header: 'Caller',
    render: (t, r) => <span>{r.caller}</span>,
  }, {
    key: 'number',
    header: 'Shop / number',
    render: (t, r) => (
      <div>
        {r.shop && <span className='cs-an__shop'>{r.shop}</span>}
        <div className={r.shop ? 'cs-an__num cs-an__dim' : 'cs-an__num'}>{r.number}</div>
      </div>
    ),
  }, {
    key: 'status',
    header: 'Caller marked',
    render: (t, r) => <b>{(r.status || '').replace(/_/g, ' ')}</b>,
  }, {
    key: 'type',
    header: 'Phone shows',
    render: (t, r) => this.phoneShows(r),
  }, {
    key: 'severity',
    header: 'Mismatch',
    render: (t, r) => (
      <span className={`cs-an__sev cs-an__sev--${r.severity}`} title={r.label}>
        {MISMATCH_SHORT[r.type] || r.type}
      </span>
    ),
  }]

  renderDiscrepancies = (data: Feed) => {
    const d = data.reconcile;
    const { discCaller, discType } = this.state;

    if (!d.available) {
      return (
        <p className='cs-an__note'>
          Nothing to compare in this range — <b>{groupNum(d.logged)}</b> call
          {d.logged === 1 ? '' : 's'} marked in Calling Soul and{' '}
          <b>{groupNum(d.phone)}</b> on the phones. This check needs both systems
          in use over the same dates; it stays quiet rather than show a percentage
          built on nothing.
        </p>
      );
    }

    if (!d.checked) {
      return (
        <p className='cs-an__note'>
          <b>{groupNum(d.logged)}</b> call{d.logged === 1 ? '' : 's'} marked in this
          range, but none could be checked: the callers who marked them had no
          Diallogs activity on those days. That is missing evidence, not a
          discrepancy, so nothing is flagged.
        </p>
      );
    }

    const rows = (d.rows || []).filter((r) =>
      (!discCaller || r.caller === discCaller) && (!discType || r.type === discType));
    const picked = (d.callers || []).filter((c) => c.caller === discCaller)[0];
    const typeCounts = picked ? picked.by_type : (d.by_type || {});
    const matchPct = Math.round((100 * d.consistent) / d.checked);

    return (
      <>
        <div className='cs-stats'>
          {statTile('calls', 'Marked in Calling Soul', groupNum(d.logged),
                    `${groupNum(d.checked)} checked against the phone`)}
          {statTile('answered', 'Matches the phone', `${matchPct}%`,
                    `${groupNum(d.consistent)} of ${groupNum(d.checked)}`)}
          {statTile('alert', 'Mismatches', groupNum(d.mismatched),
                    `${d.mismatch_pct}% of checked calls`)}
          {statTile('calls', 'Not checked', groupNum(d.skipped),
                    'caller not on Diallogs that day')}
          {statTile('calls', 'Dialled, never marked', groupNum(d.phone_unlogged),
                    'work missing from Calling Soul')}
        </div>

        <p className='cs-an__note'>
          A call is a mismatch only when the phone record contradicts what was
          marked — for example <b>WRONG NUMBER</b> on a call that never connected,
          since nobody answered who could have said so. Matching allows
          ±{d.window_minutes} minutes and any caller’s phone, a mark stands if{' '}
          <b>any</b> nearby dial supports it, and days a caller wasn’t on Diallogs
          are left unchecked rather than counted against them. Treat{' '}
          <span className='warn'>amber</span> rows as questions to ask, not
          conclusions. Click a caller to see only their calls.
        </p>

        <table className='cs-an__mini cs-an__callers'>
          <thead>
            <tr>
              <th>Caller</th>
              <th className='n'>Marked</th>
              <th className='n'>Checked</th>
              <th className='n'>Mismatches</th>
              <th className='n'>%</th>
              <th>Most common</th>
            </tr>
          </thead>
          <tbody>
            {(d.callers || []).map((c) => {
              const top = MISMATCH_ORDER
                .filter((k) => c.by_type[k])
                .sort((a, b) => c.by_type[b] - c.by_type[a])[0];
              const on = discCaller === c.caller;
              return (
                <tr
                  key={c.caller_id}
                  className={`cs-an__caller-row${on ? ' cs-an__caller-row--on' : ''}`}
                  onClick={() => this.setState({ discCaller: on ? '' : c.caller })}
                  title={on ? 'Show all callers' : `Show only ${c.caller}`}
                >
                  <td><b>{c.caller}</b></td>
                  <td className='n'>{groupNum(c.logged)}</td>
                  <td className='n'>
                    {groupNum(c.checked)}
                    {c.skipped
                      ? <span className='cs-an__dim'> (+{groupNum(c.skipped)} unchecked)</span>
                      : null}
                  </td>
                  <td className='n'>
                    {c.mismatched
                      ? <span className='bad'>{groupNum(c.mismatched)}</span>
                      : <span className='cs-an__dim'>0</span>}
                  </td>
                  <td className='n'>{c.checked ? `${c.mismatch_pct}%` : '—'}</td>
                  <td>
                    {top
                      ? `${MISMATCH_SHORT[top]} (${groupNum(c.by_type[top])})`
                      : <span className='cs-an__dim'>—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className='cs-an__panel-head' style={{ marginTop: 18 }}>
          <h4>Mismatches{discCaller ? ` — ${discCaller}` : ''}</h4>
          <span className='cs-an__spacer' />
          <button
            type='button'
            className={`cs-chip${!discType ? ' cs-chip--on' : ''}`}
            onClick={() => this.setState({ discType: '' })}
          >
            All
          </button>
          {MISMATCH_ORDER.filter((k) => typeCounts[k]).map((k) => (
            <button
              key={k}
              type='button'
              className={`cs-chip${discType === k ? ' cs-chip--on' : ''}`}
              onClick={() => this.setState({ discType: discType === k ? '' : k })}
              title={d.types && d.types[k] ? d.types[k].label : ''}
            >
              {MISMATCH_SHORT[k]} ({groupNum(typeCounts[k])})
            </button>
          ))}
        </div>

        {rows.length ? (
          <>
            <Table data={rows} columns={this.mismatchColumns()} />
            {d.rows_truncated && (
              <p className='cs-rep__muted' style={{ marginTop: 8 }}>
                Showing the newest {groupNum((d.rows || []).length)} mismatches —
                narrow the dates, or choose a caller in the filter at the top, to
                see the rest.
              </p>
            )}
          </>
        ) : (
          <div className='cs-list__empty'>
            <h4>{d.mismatched ? 'No mismatches for this selection' : 'No mismatches in this range'}</h4>
            <p>
              {d.mismatched
                ? 'Clear the caller or mismatch filter to see the others.'
                : 'Every checked call matches what the phone record shows.'}
            </p>
          </div>
        )}
      </>
    );
  }

  offBookColumns = (): Array<Colums<OffBook>> => [{
    key: 'decide',
    header: '',
    render: (t, r) => (
      <button
        type='button'
        className='cs-an__decide-btn'
        onClick={() => this.openDecide(r)}
        title='Say what this number actually is'
      >
        {r.decision ? 'Change' : 'Identify'}
      </button>
    ),
  }, {
    key: 'number',
    header: 'Number',
    render: (t, r) => (
      <div>
        <span className='cs-an__num'>{r.number}</span>
        {r.name && <div className='cs-an__dim'>{r.name}</div>}
        {hasHint(r) ? r.hints.map((h, i) => (
          <div className='cs-an__hint' key={i}>
            <MdLightbulbOutline /> {h.text}
          </div>
        )) : null}
      </div>
    ),
  }, {
    key: 'callers',
    header: 'Called by',
    render: (t, r) => <span>{r.callers || <span className='cs-an__dim'>—</span>}</span>,
  }, {
    key: 'calls',
    header: 'Attempts',
    render: (t, r) => <span className='cs-rep__num'>{groupNum(r.calls)}</span>,
  }, {
    key: 'answered',
    header: 'Answered',
    render: (t, r) => (
      r.answered
        ? <span className='cs-rep__num ok'>{groupNum(r.answered)}</span>
        : <span className='cs-rep__num cs-rep__num--zero'>never</span>
    ),
  }, {
    key: 'talk_seconds',
    header: 'Talk time',
    render: (t, r) => (
      r.talk_seconds
        ? <span className='cs-rep__num'>{shortDuration(r.talk_seconds)}</span>
        : <span className='cs-rep__num cs-rep__num--zero'>—</span>
    ),
  }, {
    key: 'last_at',
    header: 'Seen',
    render: (t, r) => (
      <span className='cs-an__dim'>
        {r.first_at === r.last_at ? r.first_at : `${r.first_at} – ${r.last_at}`}
      </span>
    ),
  }]

  columns = (): Array<Colums<RecentCall>> => [{
    key: 'at',
    header: 'Time',
    render: (t, c) => <span className='cs-an__num'>{c.at}</span>,
  }, {
    key: 'caller',
    header: 'Caller',
    render: (t, c) => (
      c.caller === 'Unattributed'
        ? <span className='cs-an__dim'>Unattributed</span>
        : <span>{c.caller}</span>
    ),
  }, {
    key: 'number',
    header: 'Shop / number',
    render: (t, c) => (
      c.shop
        ? (
          <div>
            <span className='cs-an__shop'>{c.shop}</span>
            <div className='cs-an__num cs-an__dim'>{c.number}</div>
          </div>
        )
        : (
          <div>
            <span className='cs-an__num'>{c.number}</span>
            {c.name && <div className='cs-an__dim'>{c.name}</div>}
          </div>
        )
    ),
  }, {
    key: 'type',
    header: 'Type',
    render: (t, c) => <span>{c.type}</span>,
  }, {
    key: 'duration',
    header: 'Duration',
    render: (t, c) => this.duration(c),
  }, {
    key: 'recording_url',
    header: 'Recording',
    render: (t, c) => {
      if (!c.recording_url) { return <span className='cs-an__dim'>—</span>; }
      // Opens the call in a modal that plays it, with its AI summary beside.
      return (
        <button
          type='button'
          className='cs-an__play'
          onClick={() => this.showCall(c, true)}
          aria-label='Play recording'
          aria-haspopup='dialog'
          title={c.insights ? 'Play recording, with its AI summary' : 'Play recording'}
        >
          <FaPlay />
        </button>
      );
    },
  }, {
    key: 'insights',
    header: 'AI summary',
    render: (t, c) => {
      if (!c.insights) {
        return (
          <span
            className='cs-an__dim'
            title={c.recording_url
              ? 'Diallogs has not produced an AI summary for this call'
              : 'No recording, so no AI summary'}
          >
            —
          </span>
        );
      }
      return (
        <button
          type='button'
          className='cs-an__ins-btn'
          onClick={() => this.showCall(c, false)}
          aria-haspopup='dialog'
        >
          Summary
        </button>
      );
    },
  }]

  render() {
    const {
      data, loading, error, fromDate, toDate, caller,
      appliedFrom, appliedTo, appliedCaller, tab,
    } = this.state;
    const validRange = this.rangeIsValid();
    const dirty = this.isDirty();

    return (
      <div className='cs-list'>
        <Page
          title='Call Analytics'
          breadcrumbs={this.breadcrumbs()}
          backIcon={<Link to='/dashboard'><MdArrowBack /></Link>}
        >
          <Card>
            <RangePresets
              presets={RANGES}
              activeKey={activePresetKey(appliedFrom, appliedTo, RANGES)}
              onPick={this.applyPreset}
            />

            {/* Every control is wrapped in .cs-rep__field so it occupies
                exactly one grid cell -- sha-el's Input renders a container
                AND a help div as siblings, which otherwise become two
                separate columns. */}
            <div className='cs-rep__filters' onKeyDown={this.onKeyDown}>
              <div className='cs-rep__field'>
                <Input
                  type='date'
                  label='From Date'
                  value={fromDate}
                  onChange={(e) => this.setState({ fromDate: e.target.value })}
                />
              </div>
              <div className='cs-rep__field'>
                <Input
                  type='date'
                  label='To Date'
                  value={toDate}
                  onChange={(e) => this.setState({ toDate: e.target.value })}
                />
              </div>
              {/* Driven by the feed, not by a GraphQL query over every Calling
                  Soul caller: only the people who actually have Diallogs rows
                  can be chosen, so an empty result always means "no calls in
                  this range", never "this person was never going to appear". */}
              <div className='cs-rep__field'>
                <label className='cs-an__label' htmlFor='cs-an-caller'>Caller</label>
                <select
                  id='cs-an-caller'
                  className='cs-an__select'
                  value={caller}
                  onChange={(e) => this.setState({ caller: e.target.value })}
                >
                  <option value=''>
                    All callers{data ? ` (${data.callers.length})` : ''}
                  </option>
                  {data && data.callers.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.name} ({groupNum(c.calls)})
                    </option>
                  ))}
                </select>
              </div>
              <button
                type='button'
                className='cs-rep__search'
                onClick={this.search}
                disabled={!validRange}
              >
                Search
              </button>
            </div>

            <div className='cs-an__bar'>
              <p className='cs-rep__scope' style={{ margin: 0 }}>
                <span>
                  Calls {data ? <b>{data.range}</b> : null}
                  {appliedCaller
                    ? <> for <b>{this.callerName(appliedCaller)}</b></>
                    : ' for all callers'}.
                </span>
                {!validRange && <span className='cs-rep__warn'>From Date is after To Date</span>}
                {validRange && dirty && <span className='cs-rep__dirty'>Press Search to apply</span>}
              </p>
              <span className='cs-an__spacer' />
              {data && this.syncLine()}
              {data && this.backupChip()}
              <button
                type='button'
                className='cs-an__refresh'
                onClick={this.downloadScorecard}
                disabled={!data || this.state.scoring}
                title={'Per-caller KPIs for these dates, as a PDF. '
                  + 'Pick "Last 7 days" for the weekly scorecard.'}
              >
                {this.state.scoring ? 'Preparing…' : 'Download scorecard (PDF)'}
              </button>
              <button
                type='button'
                className='cs-an__refresh'
                onClick={this.downloadReport}
                disabled={!data || this.state.reporting}
                title={'The figures above plus a day-by-day table '
                  + '(calls, connected, talk time), as a PDF.'}
              >
                {this.state.reporting ? 'Preparing…' : 'Download report (PDF)'}
              </button>
              <button
                type='button'
                className='cs-an__refresh'
                onClick={() => this.load()}
                disabled={loading}
              >
                {loading ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>

            {this.state.scoreError ? (
              <p className='cs-an__note cs-an__note--bad'>{this.state.scoreError}</p>
            ) : null}

            {this.state.reportError ? (
              <p className='cs-an__note cs-an__note--bad'>{this.state.reportError}</p>
            ) : null}

            {this.renderBackup()}

            {error && (
              <div className='cs-list__empty'>
                <h4>Could not load the call data</h4>
                <p>{error}</p>
              </div>
            )}

            {!error && loading && !data && <Loading isLoading />}

            {!error && data && (
              <>
                <div className='cs-stats'>
                  {statTile('calls', 'Total calls', groupNum(data.total),
                            data.range)}
                  {statTile('answered', 'Connected', groupNum(data.connected),
                            'someone picked up')}
                  {statTile('alert', 'No pickup', groupNum(data.no_reply),
                            'dialled, never answered')}
                  {statTile('rate', 'Talk time', `${groupNum(data.talk_minutes)}m`,
                            'total across all calls')}
                  {statTile('rate', 'Avg talk time',
                            shortDuration(data.avg_talk_secs),
                            'per connected call')}
                  {statTile('calls', 'Calls per day',
                            (data.avg_calls_per_day || 0).toFixed(1),
                            `over ${groupNum(data.active_days)} day`
                            + `${data.active_days === 1 ? '' : 's'} with calls`)}
                  {statTile('calls', 'Unique numbers',
                            groupNum(data.unique_numbers), 'distinct people')}
                  {statTile('calls', 'With recording',
                            groupNum(data.with_recording), 'audio available')}
                  {statTile('answered', 'Matched to a shop',
                            groupNum(data.matched_shops),
                            'number found in the retailer list')}
                </div>

                {/* Tabs, not four stacked sections. The call log and the
                    unknown-numbers list are each up to 500 rows, so reaching
                    the last section meant scrolling past a thousand. Only the
                    selected panel is rendered, so the tables you are not
                    looking at cost nothing to mount either. */}
                <div className='cs-an__tabs' role='tablist'>
                  {[
                    { key: 'calls', label: 'Calls', n: data.total },
                    { key: 'numbers', label: 'Unknown numbers', n: data.off_book_counts.all },
                    { key: 'breakdown', label: 'Breakdown', n: null },
                    { key: 'reconcile', label: 'Discrepancies', n: data.reconcile.available ? (data.reconcile.mismatched || 0) : null },
                  ].map((t) => (
                    <button
                      key={t.key}
                      type='button'
                      role='tab'
                      aria-selected={tab === t.key}
                      className={`cs-an__tab${tab === t.key ? ' cs-an__tab--on' : ''}`}
                      onClick={() => this.setState({ tab: t.key })}
                    >
                      {t.label}
                      {t.n !== null && <span className='cs-an__tab-n'>{groupNum(t.n)}</span>}
                    </button>
                  ))}
                </div>

                {tab === 'breakdown' && <div className='cs-an__cols'>
                  <div className='cs-an__panel'>
                    <h4>By caller</h4>
                    <table className='cs-an__mini'>
                      <thead>
                        <tr>
                          <th>Caller</th>
                          <th className='n'>Calls</th>
                          <th className='n'>Talk (min)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.per_caller.length ? data.per_caller.map((c) => (
                          <tr key={c.caller}>
                            <td>
                              {c.caller === 'Unattributed'
                                ? <span className='cs-an__dim'>Unattributed</span>
                                : c.caller}
                            </td>
                            <td className='n'>{groupNum(c.calls)}</td>
                            <td className='n'>{groupNum(c.talk_minutes)}</td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan={3} className='cs-an__dim'>
                              No calls in this range
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className='cs-an__panel'>
                    <h4>By call type</h4>
                    <table className='cs-an__mini'>
                      <thead>
                        <tr><th>Type</th><th className='n'>Count</th></tr>
                      </thead>
                      <tbody>
                        {data.by_type.length ? data.by_type.map((t) => (
                          <tr key={t.label}>
                            <td>{t.label}</td>
                            <td className='n'>{groupNum(t.count)}</td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan={2} className='cs-an__dim'>
                              No calls in this range
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>}

                {tab === 'reconcile' && <div className='cs-an__panel' style={{ marginTop: 18 }}>
                  <div className='cs-an__panel-head'>
                    <h4>What callers marked vs what the phone shows</h4>
                  </div>
                  {this.renderDiscrepancies(data)}
                </div>}

                {tab === 'calls' && <div className='cs-an__panel' style={{ marginTop: 18 }}>
                  <div className='cs-an__panel-head'>
                    <h4>
                      {this.state.insightsOnly ? 'Calls with an AI summary'
                        : this.state.recordedOnly ? 'Calls with a recording' : 'Recent calls'}
                    </h4>
                    <span className='cs-an__spacer' />
                    <button
                      type='button'
                      className={`cs-chip${!this.state.recordedOnly && !this.state.insightsOnly
                        ? ' cs-chip--on' : ''}`}
                      aria-pressed={!this.state.recordedOnly && !this.state.insightsOnly}
                      onClick={() => this.setListMode('recent')}
                    >
                      Most recent
                    </button>
                    <button
                      type='button'
                      className={`cs-chip${this.state.recordedOnly ? ' cs-chip--on' : ''}`}
                      aria-pressed={this.state.recordedOnly}
                      onClick={() => this.setListMode('recorded')}
                      disabled={!data.with_recording}
                      title={data.with_recording
                        ? 'List only the calls that have audio'
                        : 'No call in this range has a recording'}
                    >
                      <FaPlay style={{ fontSize: 9, marginRight: 5 }} />
                      With recording ({groupNum(data.with_recording)})
                    </button>
                    <button
                      type='button'
                      className={`cs-chip${this.state.insightsOnly ? ' cs-chip--on' : ''}`}
                      aria-pressed={this.state.insightsOnly}
                      onClick={() => this.setListMode('insights')}
                      disabled={!data.with_insights && !this.state.insightsOnly}
                      title={data.with_insights
                        ? 'List only the calls Diallogs wrote an AI summary for'
                        : 'No call in this range has an AI summary yet'}
                    >
                      With AI summary ({groupNum(data.with_insights || 0)})
                    </button>
                  </div>

                  {data.recent.length ? (
                    <>
                      <Table data={data.recent} columns={this.columns()} />
                      {data.recent_truncated && (
                        <p className='cs-rep__muted' style={{ marginTop: 8 }}>
                          Showing the first {groupNum(data.recent.length)} —
                          narrow the range to see the rest.
                        </p>
                      )}
                    </>
                  ) : this.state.insightsOnly ? (
                    <div className='cs-list__empty'>
                      <h4>No AI summaries in this range</h4>
                      <p>
                        Diallogs writes a summary only for calls it has a recording
                        of, and began doing so on 19 Sep 2026. Try a wider range.
                      </p>
                    </div>
                  ) : this.state.recordedOnly ? (
                    <div className='cs-list__empty'>
                      <h4>No recordings in this range</h4>
                      <p>
                        Diallogs supplies audio for only some calls
                        {data.total ? ` — none of the ${groupNum(data.total)} here` : ''}.
                        Try a wider range.
                      </p>
                    </div>
                  ) : (
                    <div className='cs-list__empty'>
                      <h4>No calls in this period</h4>
                      <p>
                        {appliedCaller
                          ? 'That caller placed no calls in this range. Try a wider '
                            + 'range, or clear the caller filter.'
                          : 'No calls in this range. Widen the dates — or if nothing '
                            + 'has been imported at all, run sync_diallogs on the server.'}
                      </p>
                    </div>
                  )}
                </div>}

                {tab === 'numbers' && <div className='cs-an__panel' style={{ marginTop: 18 }}>
                  <div className='cs-an__panel-head'>
                    <h4>Numbers not in the retailer master</h4>
                    <span className='cs-an__spacer' />
                    {[
                      { key: 'answered', label: 'Spoke to them', n: data.off_book_counts.answered },
                      { key: 'dead', label: 'Never answered', n: data.off_book_counts.dead },
                      { key: 'all', label: 'All', n: data.off_book_counts.all },
                      { key: 'decided', label: 'Identified', n: data.off_book_counts.decided },
                      { key: 'clues', label: 'With a clue', n: data.off_book_counts.clues || 0 },
                    ].map((m) => (
                      <button
                        key={m.key}
                        type='button'
                        className={`cs-chip${data.off_book_mode === m.key ? ' cs-chip--on' : ''}`}
                        aria-pressed={data.off_book_mode === m.key}
                        onClick={() => this.setOffBookMode(m.key)}
                        disabled={!m.n}
                      >
                        {m.label} ({groupNum(m.n)})
                      </button>
                    ))}
                    <button
                      type='button'
                      className={`cs-chip${this.state.queueMode ? ' cs-chip--on' : ''}`}
                      onClick={() => this.setQueueMode(!this.state.queueMode, data)}
                      disabled={!data.off_book.length && !this.state.queueMode}
                      title='Work through these one at a time instead of scrolling a list'
                    >
                      {this.state.queueMode ? 'Back to the list' : 'Review one by one'}
                    </button>
                    <button
                      type='button'
                      className='cs-chip'
                      onClick={this.downloadExport}
                      disabled={this.state.exporting || !data.decisions_total}
                      title={data.decisions_total
                        ? `Download all ${data.decisions_total} identified `
                          + 'number(s) as a CSV, for whoever maintains the '
                          + 'retailer master. Ignores the date filter.'
                        : 'Nothing identified yet — review a number first'}
                    >
                      {this.state.exporting
                        ? 'Preparing…'
                        : `Export for Beat-Node (${groupNum(data.decisions_total || 0)})`}
                    </button>
                  </div>

                  {this.state.exportError ? (
                    <p className='cs-an__note cs-an__note--bad'>
                      {this.state.exportError}
                    </p>
                  ) : null}

                  {this.state.queueMode
                    ? this.renderQueue(data)
                    : this.renderDecide(data)}

                  {this.state.queueMode ? null : data.off_book.length ? (
                    <>
                      <p className='cs-an__note'>
                        {data.off_book_mode === 'dead' ? (
                          <>
                            Dialled <b>two or more times</b> and never once answered —
                            candidates for correction or retirement. A single missed
                            attempt proves nothing, so it takes two to appear here.
                          </>
                        ) : data.off_book_mode === 'decided' ? (
                          <>
                            Numbers somebody has <b>already identified</b>. They
                            stay off the working list for good, and the ones
                            linked to a shop now count towards that shop.
                            Identifying a number fixes the figures on this
                            screen — it does <b>not</b> change the retailer
                            master. Use <b>Export for Beat-Node</b> above to
                            hand these over to whoever maintains it.
                          </>
                        ) : data.off_book_mode === 'clues' ? (
                          <>
                            Numbers with a <b>clue</b> to what they are — one of our
                            staff&apos;s phones, a likely misdial of a shop the caller
                            then reached, a number held by two shop records, and so
                            on. The quickest to clear: open one, check the clue, and
                            where it offers a suggestion, accept it with one click.
                            Clues are likely, not certain, so check each one.
                          </>
                        ) : data.off_book_mode === 'answered' ? (
                          <>
                            Numbers somebody <b>actually spoke to</b> that no retailer
                            in the master holds, most-talked-to first — the likeliest
                            missing shops. The saved contact names show this is a
                            genuine mix: agencies and offices alongside clearly
                            personal contacts, because the Diallogs app records{' '}
                            <b>every</b> call on the device, not only work ones. Check
                            the name before adding anything to the master.
                          </>
                        ) : (
                          <>
                            Every number dialled that no retailer in the master holds,
                            most-talked-to first — missing shops, wrong numbers and
                            personal calls together. The other two views split this
                            into something you can act on.
                          </>
                        )}
                      </p>
                      <Table data={data.off_book} columns={this.offBookColumns()} />
                      {data.off_book_truncated && (
                        <p className='cs-rep__muted' style={{ marginTop: 8 }}>
                          Showing the top {groupNum(data.off_book.length)} of{' '}
                          {groupNum(data.off_book_total)} by talk time — narrow the
                          range or the caller to see the rest.
                        </p>
                      )}
                    </>
                  ) : (
                    <div className='cs-list__empty'>
                      <h4>
                        {data.off_book_counts.all
                          ? 'Nothing in this view'
                          : 'Every number is in the master'}
                      </h4>
                      <p>
                        {data.off_book_counts.all
                          ? 'No number in this range matches that filter. Try one of '
                            + 'the other views.'
                          : 'No call in this range went to a number the retailer '
                            + 'master does not already hold.'}
                      </p>
                    </div>
                  )}
                </div>}
              </>
            )}
          </Card>
        </Page>
        {this.state.openCall && (
          <CallDialog
            key={this.state.openCall.id}
            call={this.state.openCall}
            autoPlay={this.state.openToPlay}
            onClose={this.hideCall}
          />
        )}
      </div>
    );
  }
}
